<template>
  <form
    class="mt-800"
    @submit.prevent="handleSaveExecutionDaySettingButtonClick"
  >
    <fieldset
      class="setting-fieldset"
      aria-describedby="execution-type-desc"
    >
      <legend class="setting-title">
        {{ $t('corpSettingApi.account.executionType.title') }}
      </legend>
      <p
        id="execution-type-desc"
        class="explain"
      >
        {{ $t('corpSettingApi.account.executionType.explain') }}
      </p>
      <div
        v-for="item in executionTypes"
        :key="item.label"
        class="mb-100"
      >
        <BRadio
          v-model="executionTypeValue"
          :label="item.label"
        >
          {{ item.text }}
        </BRadio>
      </div>
    </fieldset>
    <BListItem class="mt-400">
      <BBtn
        :disabled="!isValid"
        type="primary"
        btn-type="submit"
        :loading="$wait.is('patchExecutionDaySetting')"
      >
        {{ $t('general.save.text') }}
      </BBtn>
    </BListItem>
  </form>
</template>

<script setup lang="ts">
import { camelCase } from 'lodash';
import { computed, inject, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { ExecutionDaySettingExecutionDayTypeEnum } from '@/api/openapi';
import { ExecutionDaySettingApiService } from '@/api/user/resources/execution_day_setting';
import { useWait } from '@/composable/vue-wait';
import { KEY_OF_TOAST } from '@/injection-keys';

const i18n = useI18n();
const { doActionWithWait } = useWait();
const toast = inject(KEY_OF_TOAST);
const api = new ExecutionDaySettingApiService();

const executionTypes = Object.values(ExecutionDaySettingExecutionDayTypeEnum).map(label => ({
  label,
  text: i18n.t(`corpSettingApi.account.executionType.executionTypes.${camelCase(label)}`),
}));

const executionTypeValue = ref<ExecutionDaySettingExecutionDayTypeEnum>('only_on_weekdays');
const isValid = computed(() => executionTypeValue.value != null);

const fetchExecutionDaySetting = async () => {
  await doActionWithWait('fetchExecutionDaySetting', async () => {
    const { data: { executionDaySetting } } = await api.getExecutionDaySetting();
    executionTypeValue.value = executionDaySetting.executionDayType;
  });
};
onBeforeMount(async () => {
  await fetchExecutionDaySetting();
});

const handleSaveExecutionDaySettingButtonClick = async () => {
  const api = new ExecutionDaySettingApiService();
  await doActionWithWait('patchExecutionDaySetting', async () => {
    await api.patchExecutionDaySetting({
      request: {
        executionDaySetting: {
          executionDayType: executionTypeValue.value,
        },
      },
    });
  });
  toast.show(
    i18n.t('general.done.withTarget', {
      action: i18n.t('general.save.text'),
      target: i18n.t('corpSettingApi.account.executionType.title'),
    }),
  );
};
</script>

<style lang="scss" scoped>
.setting-fieldset {
  border: none;
}

.setting-title {
  font-size: $fontsize-400;
  font-weight: bold;
  margin-bottom: $basespace-150;
}
.explain {
  color: $textcolor-light;
  white-space: pre-wrap;
  margin-bottom: $basespace-400;
}
</style>
